import { NodeAxios } from '../api'

export type InterviewCompileProfileType = 'heft' | 'foundation' | 'identity'
export type InterviewCompileSubjectKind = 'person' | 'company' | 'product' | 'group'
export type InterviewCompileTargetField = { path: string; label?: string }
export type InterviewCompileSource =
    | { kind: 'document'; documentId: string; documentVersion?: string }
    | { kind: 'paste'; text: string; title?: string }
    | { kind: 'interview_session'; sessionId: string }

export type InterviewCompileJob = {
    jobId: string
    targetProfileId: string
    targetProfileType: InterviewCompileProfileType
    targetSubjectKind?: InterviewCompileSubjectKind
    developmentRunId?: string
    targetFields?: InterviewCompileTargetField[]
    status: 'queued' | 'running' | 'review_ready' | 'failed' | 'partially_failed' | 'cancelled'
    stage?: string
    progress?: { completed: number; total: number; label: string }
    candidateCount?: number
    artifactId?: string
    artifactVersion?: number
}

export type InterviewCompileCandidate = {
    candidateId: string
    developmentRunId?: string
    targetFields?: InterviewCompileTargetField[]
    objectType: string
    compiledValue: Record<string, unknown>
    proposedValue?: Record<string, unknown>
    currentProfileValue?: Record<string, unknown>
    currentValue: Record<string, unknown>
    reviewStatus: 'pending' | 'accepted' | 'rejected'
    applyStatus: 'not_started' | 'queued' | 'applied' | 'failed'
    applyError?: string
    candidateState?: 'active' | 'superseded' | 'invalidated'
    disposition: 'new' | 'update' | 'duplicate' | 'conflict' | 'related'
    revision: number
    wasEdited: boolean
    evidenceRefs: string[]
    destinationSnapshot?: {
        destinationKeys?: string[]
        adapterVersions?: Array<{ adapterId: string; version: string }>
        capturedAt?: string
    }
    matchedExistingEntityIds?: string[]
    matchedExistingValues?: Array<{ id: string; text: string }>
    sourceEvidenceRefs: Array<{
        sourceSnapshotId: string
        evidenceId?: string
        normalizedStart?: number
        normalizedEnd?: number
        originalStart?: number
        originalEnd?: number
        turnId?: string
        speakerId?: string
        excerpt: string
    }>
}

export type InterviewApplyRun = {
    applyRunId: string
    status: 'queued' | 'running' | 'completed' | 'partially_failed' | 'failed'
    candidateIds: string[]
    receipts: Array<{ candidateId: string; status: 'applied' | 'failed' | 'skipped'; error?: string }>
    counts: { applied: number; failed: number; skipped: number; created: number; updated: number; duplicate: number }
}

function idempotencyKey(prefix: string) {
    return `${prefix}_${crypto.randomUUID()}`
}

export const interviewCompileService = {
    async start(params: {
        targetProfileId: string
        targetProfileType: InterviewCompileProfileType
        targetSubjectKind?: InterviewCompileSubjectKind
        source: InterviewCompileSource
        idempotencyKey?: string
        reprocess?: boolean
        deferProcessing?: boolean
    }): Promise<{ success: boolean; job: InterviewCompileJob; idempotentReplay: boolean }> {
        const idempotencyKeyValue = params.idempotencyKey ?? idempotencyKey('icomp')
        const result = await NodeAxios.post(
            '/campaigns/interview-compile/jobs',
            {
                ...params,
                idempotencyKey: idempotencyKeyValue,
            },
            { headers: { 'Idempotency-Key': idempotencyKeyValue } }
        )
        return result.data || result
    },

    async getJob(jobId: string): Promise<{ success: boolean; job: InterviewCompileJob }> {
        const result = await NodeAxios.get(`/campaigns/interview-compile/jobs/${encodeURIComponent(jobId)}`)
        return result.data || result
    },

    async cancel(jobId: string): Promise<{ success: boolean; cancelled: boolean; job: InterviewCompileJob }> {
        const result = await NodeAxios.post(`/campaigns/interview-compile/jobs/${encodeURIComponent(jobId)}/cancel`, {})
        return result.data || result
    },

    async listActive(
        profileId: string,
        scope?: { profileType?: InterviewCompileProfileType; subjectKind?: InterviewCompileSubjectKind },
    ): Promise<{ success: boolean; jobs: InterviewCompileJob[] }> {
        const query = new URLSearchParams()
        if (scope?.profileType) query.set('profileType', scope.profileType)
        if (scope?.subjectKind) query.set('subjectKind', scope.subjectKind)
        const suffix = query.toString() ? `?${query.toString()}` : ''
        const result = await NodeAxios.get(
            `/campaigns/interview-compile/profiles/${encodeURIComponent(profileId)}/jobs/active${suffix}`
        )
        return result.data || result
    },

    async listCandidates(jobId: string): Promise<{ success: boolean; candidates: InterviewCompileCandidate[] }> {
        const result = await NodeAxios.get(`/campaigns/interview-compile/jobs/${encodeURIComponent(jobId)}/candidates`)
        return result.data || result
    },

    async reviewCandidate(params: {
        jobId: string
        candidateId: string
        action: 'accept' | 'reject' | 'edit' | 'reset'
        expectedRevision: number
        reviewStatus?: InterviewCompileCandidate['reviewStatus']
        currentValue?: Record<string, unknown>
    }) {
        const result = await NodeAxios.patch(
            `/campaigns/interview-compile/jobs/${encodeURIComponent(params.jobId)}/candidates/${encodeURIComponent(params.candidateId)}`,
            params
        )
        return result.data || result
    },

    async undoCandidate(jobId: string, candidateId: string) {
        const result = await NodeAxios.post(
            `/campaigns/interview-compile/jobs/${encodeURIComponent(jobId)}/candidates/${encodeURIComponent(candidateId)}/undo`,
            {}
        )
        return result.data || result
    },

    async apply(jobId: string, candidateIds: string[], candidateRevisions: Record<string, number>, key?: string) {
        const idempotencyKeyValue = key ?? idempotencyKey('iapply')
        const result = await NodeAxios.post(
            `/campaigns/interview-compile/jobs/${encodeURIComponent(jobId)}/apply`,
            { candidateIds, candidateRevisions, idempotencyKey: idempotencyKeyValue },
            { headers: { 'Idempotency-Key': idempotencyKeyValue } }
        )
        return result.data || result
    },

    async getApplyRun(applyRunId: string): Promise<{ success: boolean; run: InterviewApplyRun }> {
        const result = await NodeAxios.get(`/campaigns/interview-compile/apply-runs/${encodeURIComponent(applyRunId)}`)
        return result.data || result
    },
}
